"use client";

import { useEffect, useRef, useState } from "react";
import type { Map as MapLibreMap, Marker as MapLibreMarker } from "maplibre-gl";

const views = [
  { id: "superior", label: "Lake Superior", note: "The world’s largest freshwater lake by surface area", center: [-87.45, 47.45] as [number, number], zoom: 5.15 },
  { id: "royale", label: "Isle Royale", note: "A 45-mile main island with hundreds of smaller islands", center: [-88.86, 48.02] as [number, number], zoom: 8.35 },
  { id: "kidd", label: "Capt. Kidd Island", note: "A small island tucked along Isle Royale’s remote north shore", center: [-88.605, 48.155] as [number, number], zoom: 11.75 },
];

const landmarks = [
  { name: "Capt. Kidd Island", coordinates: [-88.5676111, 48.1661389] as [number, number], primary: true },
  { name: "Amygdaloid Island", coordinates: [-88.630952, 48.1472421] as [number, number] },
  { name: "Belle Isle", coordinates: [-88.6056055, 48.148883] as [number, number] },
  { name: "Johnson Island", coordinates: [-88.5838425, 48.1478638] as [number, number] },
  { name: "Crystal Cove", coordinates: [-88.5915, 48.1575] as [number, number] },
];

export default function LocationJourneyMap() {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const markersRef = useRef<MapLibreMarker[]>([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    let cancelled = false;

    import("maplibre-gl").then((maplibregl) => {
      if (cancelled || !containerRef.current) return;
      const map = new maplibregl.Map({
        container: containerRef.current,
        style: {
          version: 8,
          sources: {
            openStreetMap: {
              type: "raster",
              tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
              tileSize: 256,
              attribution: "© OpenStreetMap contributors",
              maxzoom: 19,
            },
          },
          layers: [
            {
              id: "paper",
              type: "background",
              paint: { "background-color": "#d9e2dc" },
            },
            {
              id: "open-street-map",
              type: "raster",
              source: "openStreetMap",
              paint: {
                "raster-saturation": -0.48,
                "raster-contrast": -0.14,
                "raster-brightness-min": 0.18,
                "raster-brightness-max": 0.96,
              },
            },
          ],
        },
        center: views[0].center,
        zoom: views[0].zoom,
        attributionControl: false,
        cooperativeGestures: true,
      });
      map.addControl(new maplibregl.AttributionControl({ compact: true }), "bottom-right");
      map.addControl(new maplibregl.NavigationControl({ showCompass: true, showZoom: true }), "top-right");

      landmarks.forEach((landmark) => {
        const marker = document.createElement("div");
        marker.className = `journey-marker${landmark.primary ? " primary" : ""}`;
        marker.innerHTML = `<i></i><span>${landmark.name}</span>`;
        markersRef.current.push(new maplibregl.Marker({ element: marker, anchor: "left" }).setLngLat(landmark.coordinates).addTo(map));
      });
      mapRef.current = map;
    });

    return () => {
      cancelled = true;
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  const selectView = (index: number) => {
    setActive(index);
    const view = views[index];
    mapRef.current?.flyTo({ center: view.center, zoom: view.zoom, duration: 1800, essential: true });
  };

  return (
    <div className={`location-journey view-${views[active].id}`}>
      <div ref={containerRef} className="location-map-canvas" aria-label="Interactive map from Lake Superior to Capt. Kidd Island" />
      <div className="map-paper-wash" aria-hidden="true" />
      <div className="journey-controls" role="group" aria-label="Map scale">
        {views.map((view, index) => (
          <button key={view.id} className={active === index ? "active" : ""} onClick={() => selectView(index)}>
            <span>{view.label}<small>{view.note}</small></span>
          </button>
        ))}
      </div>
      <div className="coordinate-plaque"><span>Capt. Kidd Island</span><b>48°09′58.1″N · 88°34′03.4″W</b></div>
      <small className="map-instruction">Choose a scale · Drag to explore</small>
    </div>
  );
}
